import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

// Match exato com .btn-primary e .btn-ghost de ui/styles/legacy.css.
// Pills (radius 980px), font 0.8rem 500, transição com translateY no hover.
const button = cva(
  cn(
    'inline-flex items-center justify-center gap-1.5 rounded-full',
    'font-medium tracking-tight cursor-pointer',
    'transition-[background,transform,box-shadow,color] duration-150',
    'active:translate-y-0 active:scale-[0.98]',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0',
  ),
  {
    variants: {
      variant: {
        // .btn-primary — coral pill com hover lift + shadow
        primary: cn(
          'bg-[var(--accent)] text-[var(--on-solid)] border-0',
          'hover:bg-[var(--accent2)] hover:-translate-y-px',
          'hover:shadow-[0_2px_10px_rgba(217,119,87,0.3)]',
        ),
        // .btn-ghost — pill cinza-translúcido
        ghost: cn(
          'bg-[rgba(0,0,0,0.04)] text-text-2 border-0',
          'hover:bg-[rgba(0,0,0,0.08)] hover:text-text',
          'dark:bg-[rgba(255,255,255,0.07)] dark:hover:bg-[rgba(255,255,255,0.13)]',
        ),
        // Secondary = botão neutro com borda hairline (composição legacy chip + ghost)
        secondary: cn(
          'bg-elevated text-text border border-border',
          'hover:bg-surface2 hover:border-border2',
        ),
        // .btn-ghost-danger
        danger: cn(
          'bg-[rgba(220,38,38,0.06)] text-danger border border-[rgba(220,38,38,0.18)]',
          'hover:bg-[rgba(220,38,38,0.12)]',
        ),
        outline: cn(
          'bg-transparent text-text border border-border',
          'hover:bg-surface2 hover:border-border2',
        ),
      },
      size: {
        // Match com .btn-primary: padding .45rem 1.1rem, font .8rem
        sm: 'h-7 px-3 text-[0.75rem]',
        md: 'h-[34px] px-[1.1rem] text-[0.8rem]',
        lg: 'h-10 px-5 text-sm',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof button> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild, type = 'button', ...rest },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      ref={ref}
      className={cn(button({ variant, size }), className)}
      {...(asChild ? {} : { type })}
      {...rest}
    />
  );
});
